import { useMemo } from 'react';
import { Paper, Box, Typography, Chip, CircularProgress } from '@gapartners/ui';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store/hooks';
import { api } from '@/store/api';
import type { AgentQueueStatusDTO } from '@/types/agentEvents';
import type { MyRoutingProfileQueueItemDTO } from '@/types/routingProfile';

const { useGetMyRoutingProfileQueuesQuery, useGetAgentQueueStatusQuery } = api;

interface QueueInfo {
  queueId: string;
  queueName: string;
  waitingContacts: number;
  channels: string[];
}

function waitingCountForProfileQueue(
  q: MyRoutingProfileQueueItemDTO,
  statusRows: AgentQueueStatusDTO[]
): number {
  const matches = statusRows.filter(row => {
    if (row.queueId !== q.queueId) return false;
    return true;
  });
  if (matches.length === 0) return 0;
  return Math.max(...matches.map(m => m.waitingCount ?? 0));
}

/**
 * One row per queue in the signed-in user's routing profile (API order), with
 * live waiting counts from {@link useGetAgentQueueStatusQuery} for that user.
 */
function mergeProfileQueuesWithStatus(
  profileQueues: MyRoutingProfileQueueItemDTO[],
  statusRows: AgentQueueStatusDTO[]
): QueueInfo[] {
  return profileQueues.map(q => ({
    queueId: q.queueId,
    queueName: q.queueName,
    channels: q.channels,
    waitingContacts: waitingCountForProfileQueue(q, statusRows),
  }));
}

function useMyRoutingProfileQueueStatus() {
  const user = useAppSelector(s => s.auth.user);
  const agentId = user?.id;

  const { data: myQueuesData, isFetching: isFetchingMyQueues } =
    useGetMyRoutingProfileQueuesQuery();

  const { data: queueStatusData, isFetching: isFetchingQueueStatus } =
    useGetAgentQueueStatusQuery(
      { agentIds: '' },
      {
        refetchOnMountOrArgChange: true,
        pollingInterval: 30_000,
      }
    );
  const queues = useMemo(
    () =>
      mergeProfileQueuesWithStatus(
        myQueuesData?.queues ?? [],
        queueStatusData?.data ?? []
      ),
    [myQueuesData, queueStatusData]
  );

  const isLoading = isFetchingMyQueues || (!!agentId && isFetchingQueueStatus);

  return { queues, isLoading };
}

export function QueueStatusWidget() {
  const { t } = useTranslation();
  const { queues, isLoading } = useMyRoutingProfileQueueStatus();
  const totalWaiting = queues.reduce((sum, q) => sum + q.waitingContacts, 0);

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t('components.manager.currentQueueStatus')}
        </Typography>
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {t('components.manager.currentQueueStatus')}
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">
            {t('components.manager.totalWaiting')}
          </Typography>
          <Chip
            label={totalWaiting}
            color={
              totalWaiting > 5
                ? 'error'
                : totalWaiting > 0
                  ? 'warning'
                  : 'success'
            }
            size="small"
          />
        </Box>

        {queues.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            {t('components.manager.noQueuesAvailable')}
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
            }}
          >
            {queues.map(queue => (
              <Paper
                key={`${queue.queueId}-${queue.channels.join(', ')}`}
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: 'center',
                  backgroundColor:
                    queue.waitingContacts > 5
                      ? 'rgba(211, 47, 47, 0.05)'
                      : queue.waitingContacts > 0
                        ? 'rgba(251, 140, 0, 0.05)'
                        : 'rgba(76, 175, 80, 0.05)',
                  borderColor:
                    queue.waitingContacts > 5
                      ? '#d32f2f'
                      : queue.waitingContacts > 0
                        ? '#fb8c00'
                        : '#4caf50',
                  borderWidth: 2,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {queue.queueName}
                </Typography>
                {queue.channels.length > 0 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {queue.channels.join(', ')}
                  </Typography>
                ) : null}
                <Typography variant="h4" fontWeight={600}>
                  {queue.waitingContacts}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('components.manager.contactsWaiting')}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          {t('components.manager.updatesEvery60Seconds')}
        </Typography>
      </Box>
    </Paper>
  );
}
